import React, { useState, useEffect, useContext, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { listarSolicitacoesCardapio, atualizarStatusSolicitacaoCardapio } from '../../services/api';
import { AuthContext } from '../../contexts/auth';
import { SidebarContext } from '../../AppRoutes';
import PermissionGate from '../../services/PermissionGate';
import DeliveryLoading from '../../components/DeliveryLoading';
import './styles.css';
import '../../styles/page-titles.css';
import regionConfig from '../../config/regionConfig';

const ContainerWithSidebar = styled.div`
    display: flex;
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    transition: margin-left 200ms ease;
    background: #ffffff;
    overflow: initial;
    z-index: 9;
    padding: 24px;

    @media (max-width: 880px) {
        margin-left: 0;
        padding: 16px;
    }
`;

const AdminSolicitacoesCardapio = () => {
    const { t } = useTranslation('adminMenuRequests');
    const isDevelopment = window.location.hostname === "localhost" ? true : false;
    const apiUrl = isDevelopment
        ? process.env.REACT_APP_SERVER_URL_DEV
        : process.env.REACT_APP_SERVER_URL_PROD + '/';
    const { user } = useContext(AuthContext);
    const { sidebar } = useContext(SidebarContext);
    const [solicitacoes, setSolicitacoes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filtroStatus, setFiltroStatus] = useState('PENDENTE');
    const [paginacao, setPaginacao] = useState({
        current: 1,
        total: 1,
        count: 0,
        totalItems: 0
    });

    // Estados para modal de atualização
    const [showModal, setShowModal] = useState(false);
    const [solicitacaoSelecionada, setSolicitacaoSelecionada] = useState(null);
    const [novoStatus, setNovoStatus] = useState('');
    const [observacoesEquipe, setObservacoesEquipe] = useState('');

    const carregarSolicitacoes = useCallback(async () => {
        if (!user || !user._id) return;

        try {
            setLoading(true);
            const response = await listarSolicitacoesCardapio(
                filtroStatus,
                paginacao.current,
                10
            );

            process.env.REACT_APP_ISDEVELOP === 'true' && console.log("📡 Resposta da API:", response.data);

            if (response.data.solicitacoes) {
                setSolicitacoes(response.data.solicitacoes);
                setPaginacao(response.data.pagination);
                process.env.REACT_APP_ISDEVELOP === 'true' && console.log('📊 Dados de paginação:', response.data.pagination);
                process.env.REACT_APP_ISDEVELOP === 'true' && console.log('📋 Solicitações carregadas:', response.data.solicitacoes.length);
            }
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao carregar solicitações:', error);
            toast(t('toasts.loadError'), { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [user, filtroStatus, paginacao.current]);

    useEffect(() => {
        if (user && user._id) {
            carregarSolicitacoes();
        }
    }, [carregarSolicitacoes, user]);

    const handleAtualizarStatus = async () => {
        if (!solicitacaoSelecionada || !novoStatus) return;

        try {
            setLoading(true);
            const response = await atualizarStatusSolicitacaoCardapio(
                solicitacaoSelecionada._id,
                novoStatus
            );

            if (response.data.msg) {
                toast(t('toasts.updateSuccess'), { type: 'success' });
                setShowModal(false);
                setSolicitacaoSelecionada(null);
                setNovoStatus('');
                setObservacoesEquipe('');
                carregarSolicitacoes(); // Recarregar lista
            }
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao atualizar status:', error);
            toast(t('toasts.updateError'), { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const abrirModal = (solicitacao) => {
        setSolicitacaoSelecionada(solicitacao);
        setNovoStatus(solicitacao.status);
        setObservacoesEquipe(solicitacao.observacoes || '');
        setShowModal(true);
    };

    const handleViewPDF = (solicitacao) => {
        if (!user || !user._id) {
            toast(t('toasts.userNotAuthenticated'), { type: 'error' });
            return;
        }

        try {
            // Verificar se tem URL direta do Google Cloud Storage
            if (solicitacao && solicitacao.arquivo && solicitacao.arquivo.url) {
                process.env.REACT_APP_ISDEVELOP === 'true' && console.log('🔗 Abrindo PDF direto do GCS:', solicitacao.arquivo.url);

                // Abrir PDF diretamente do Google Cloud Storage
                const newWindow = window.open(solicitacao.arquivo.url, '_blank');

                if (!newWindow) {
                    toast(t('toasts.popupBlocked'), { type: 'warning' });
                }
            } else {
                // Fallback para nova URL do backend (arquivos antigos)
                const pdfUrl = `${apiUrl}api/v1/admin/solicitacoes-cardapio/${solicitacao._id}/download`;

                process.env.REACT_APP_ISDEVELOP === 'true' && console.log('🔗 Abrindo PDF via backend:', pdfUrl);

                const newWindow = window.open(pdfUrl, '_blank');

                if (!newWindow) {
                    toast(t('toasts.popupBlocked'), { type: 'warning' });
                }
            }
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao visualizar PDF:', error);
            toast(t('toasts.pdfViewError'), { type: 'error' });
        }
    };



    const formatarData = (data) => {
        return new Date(data).toLocaleString(regionConfig.currency.locale);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'PENDENTE': return '#f39c12';
            case 'EM_ANDAMENTO': return '#3498db';
            case 'CONCLUIDO': return '#27ae60';
            case 'REJEITADO': return '#e74c3c';
            default: return '#95a5a6';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'PENDENTE': return t('status.pending');
            case 'EM_ANDAMENTO': return t('status.inProgress');
            case 'CONCLUIDO': return t('status.completed');
            case 'REJEITADO': return t('status.rejected');
            default: return status;
        }
    };

    return (
        <PermissionGate permissions={['default']}>
            <ContainerWithSidebar sidebar={sidebar}>
                {loading && (
                    <DeliveryLoading inline message={t('loadingRequests')} />
                )}
                <div className="w-100 p-4">
                    <div className="admin-header">
                        <h1>🍽️ {t('title')}</h1>
                        <p>{t('subtitle')}</p>
                    </div>

                    {/* Filtros */}
                    <div className="filtros-container">
                        <div className="filtro-grupo">
                            <label>{t('filters.statusLabel')}</label>
                            <select
                                value={filtroStatus}
                                onChange={(e) => {
                                    setFiltroStatus(e.target.value);
                                    setPaginacao(prev => ({ ...prev, current: 1 }));
                                }}
                            >
                                <option value="Todos">{t('filters.all')}</option>
                                <option value="PENDENTE">{t('filters.pending')}</option>
                                <option value="EM_ANDAMENTO">{t('filters.inProgress')}</option>
                                <option value="CONCLUIDO">{t('filters.completed')}</option>
                                <option value="REJEITADO">{t('filters.rejected')}</option>
                            </select>
                        </div>
                        <div className="filtro-grupo">
                            <label>{t('filters.itemsPerPageLabel')}</label>
                            <select
                                value="10"
                                onChange={(e) => {
                                    // Funcionalidade de itens por página pode ser implementada depois
                                    process.env.REACT_APP_ISDEVELOP === 'true' && console.log('Itens por página:', e.target.value);
                                }}
                            >
                                <option value="10">10</option>
                                <option value="20">20</option>
                                <option value="50">50</option>
                            </select>
                        </div>
                        <button onClick={carregarSolicitacoes} className="btn-atualizar" disabled={loading}>
                            {loading ? `🔄 ${t('buttons.refreshing')}` : `🔄 ${t('buttons.refresh')}`}
                        </button>
                    </div>

                    {/* Informações da Paginação */}
                    <div className="paginacao-info">
                        <div className="info-left">
                            <span>
                                {t('pagination.showing')} <strong>{solicitacoes.length}</strong> {t('pagination.of')} <strong>{paginacao.totalItems || 0}</strong> {t('pagination.requests')}
                                {loading && <span className="loading-indicator"> • {t('pagination.loading')}</span>}
                            </span>
                        </div>
                        <div className="info-right">
                            <span>
                                {t('pagination.page')} <strong>{paginacao.current || 1}</strong> {t('pagination.of')} <strong>{paginacao.total || 1}</strong>
                            </span>
                        </div>
                    </div>

                    {/* Lista de Solicitações */}
                    <div className="solicitacoes-lista">
                        {loading && <div className="asc-loading"/>}

                        {!loading && solicitacoes.length === 0 && (
                            <div className="empty-state">
                                <p>{t('emptyState.noRequests')}</p>
                            </div>
                        )}

                        {!loading && solicitacoes.map((item, index) => (
                            <div key={index} className="solicitacao-card">
                                <div className="card-header">
                                    <div className="empresa-info">
                                        <h3>{item.empresaObjId?.name || t('card.companyNotFound')}</h3>
                                        <p>📧 {item.empresaObjId?.email || t('card.emailNotAvailable')}</p>
                                        <p>📞 {item.empresaObjId?.telefone || t('card.phoneNotAvailable')}</p>
                                    </div>
                                    <div className="status-badge" style={{ backgroundColor: getStatusColor(item.status) }}>
                                        {getStatusLabel(item.status)}
                                    </div>
                                </div>

                                <div className="card-content">
                                    <div className="solicitacao-info">
                                        <p><strong>📅 {t('card.requestDate')}</strong> {formatarData(item.createdAt)}</p>

                                        {item.linkCardapio && (
                                            <p><strong>🔗 {t('card.menuLink')}</strong>
                                                <a href={item.linkCardapio} target="_blank" rel="noopener noreferrer">
                                                    {item.linkCardapio}
                                                </a>
                                            </p>
                                        )}

                                        {item.arquivo && item.arquivo.nomeOriginal && (
                                            <div className="pdf-info">
                                                <p><strong>📄 {t('card.pdfFile')}</strong> {item.arquivo.nomeOriginal}</p>
                                                {item.arquivo.tamanho && (
                                                    <p className="pdf-size">
                                                        <strong>📊 {t('card.fileSize')}</strong> {Math.round(item.arquivo.tamanho / 1024)} KB
                                                    </p>
                                                )}
                                                <div className="pdf-actions">
                                                    <button
                                                        onClick={() => handleViewPDF(item)}
                                                        className="btn-view-pdf"
                                                        title={t('buttons.viewPdf')}
                                                    >
                                                        👁️ {t('buttons.viewPdf')}
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {item.observacoes && (
                                            <p><strong>📝 {t('card.customerNotes')}</strong> {item.observacoes}</p>
                                        )}

                                        <p><strong>👤 {t('card.requestedBy')}</strong> {item.solicitadoPor?.userName || t('card.notInformed')}</p>

                                        {item.responsavelAtualizacao && (
                                            <p><strong>👨‍💼 {t('card.responsible')}</strong> {item.responsavelAtualizacao}</p>
                                        )}

                                        {item.dataAtualizacao && (
                                            <p><strong>✅ {t('card.updateDate')}</strong> {formatarData(item.dataAtualizacao)}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="card-actions">
                                    <button
                                        onClick={() => abrirModal(item)}
                                        className="btn-atualizar-status"
                                    >
                                        ✏️ {t('buttons.updateStatus')}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Controles de Paginação */}
                    <div className="paginacao">
                        <button
                            onClick={() => setPaginacao(prev => ({ ...prev, current: prev.current - 1 }))}
                            disabled={(paginacao.current || 1) === 1 || (paginacao.total || 1) <= 1}
                        >
                            ← {t('buttons.previous')}
                        </button>
                        <span>{t('pagination.page')} {paginacao.current || 1} {t('pagination.of')} {paginacao.total || 1}</span>
                        <button
                            onClick={() => setPaginacao(prev => ({ ...prev, current: prev.current + 1 }))}
                            disabled={(paginacao.current || 1) === (paginacao.total || 1) || (paginacao.total || 1) <= 1}
                        >
                            {t('buttons.next')} →
                        </button>
                    </div>

                    {/* Modal de Atualização */}
                    {showModal && (
                        <div className="modal-overlay" onClick={() => setShowModal(false)}>
                            <div className="modal-content" onClick={e => e.stopPropagation()}>
                                <div className="modal-header">
                                    <h3>{t('modal.title')}</h3>
                                    <button onClick={() => setShowModal(false)}>×</button>
                                </div>

                                <div className="modal-body">
                                    <div className="form-group">
                                        <label>{t('modal.companyLabel')}</label>
                                        <p>{solicitacaoSelecionada?.empresaObjId?.name || t('card.companyNotFound')}</p>
                                    </div>

                                    <div className="form-group">
                                        <label>{t('modal.newStatusLabel')}</label>
                                        <select
                                            value={novoStatus}
                                            onChange={(e) => setNovoStatus(e.target.value)}
                                        >
                                            <option value="PENDENTE">{t('status.pending')}</option>
                                            <option value="EM_ANDAMENTO">{t('status.inProgress')}</option>
                                            <option value="CONCLUIDO">{t('status.completed')}</option>
                                            <option value="REJEITADO">{t('status.rejected')}</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>{t('modal.teamNotesLabel')}</label>
                                        <textarea
                                            value={observacoesEquipe}
                                            onChange={(e) => setObservacoesEquipe(e.target.value)}
                                            placeholder={t('modal.teamNotesPlaceholder')}
                                            rows="4"
                                        />
                                    </div>
                                </div>

                                <div className="modal-actions">
                                    <button onClick={() => setShowModal(false)} className="btn-cancelar">
                                        {t('buttons.cancel')}
                                    </button>
                                    <button
                                        onClick={handleAtualizarStatus}
                                        className="btn-salvar"
                                        disabled={loading}
                                    >
                                        {loading ? t('buttons.saving') : t('buttons.save')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </ContainerWithSidebar>
        </PermissionGate>
    );
};

export default AdminSolicitacoesCardapio; 